export const CONSULT_STATUS_MESSAGES = {
  MALE_PARTNER: {
    PRIMARY: {
      INTAKE_INCOMPLETE: "Complete your clinical intake",
      UNDER_REVIEW: "A clinician is reviewing",
      FINALIZING: "A clinician is finalizing your treatment",
      COMPLETE: "Your treatment is ready!",
    },
    SECONDARY: {
      INTAKE_NEEDED:
        "Please complete your clinical intake to begin your consultation.",
      UNDER_REVIEW:
        "An Evvy-affiliated clinician is reviewing. We’ll reach out over email if there are any questions.",
      FINALIZING: "A clinician is finalizing your treatment recommendations.",
      INTAKE_REQUIRED:
        "An Evvy-affiliated clinician cannot review your request until you complete your clinical intake.",
    },
  },
  STANDARD: {
    PRIMARY: {
      INTAKE_INCOMPLETE: "Complete your clinical intake",
      IN_PROGRESS: "A provider is finalizing your treatments!",
      COMPLETE: "A provider has prescribed your treatment!",
      ID_VERIFICATION_FAILED: "ID verification failed",
    },
    SECONDARY: {
      DEFAULT:
        "An Evvy-affiliated provider is finalizing your treatments. We'll reach out over email if there are any questions.",
      INTAKE_REQUIRED:
        "An Evvy-affiliated provider cannot review your request until you complete your clinical intake.",
      ID_VERIFICATION_FAILED:
        "You'll need to complete this step before an Evvy-affiliated provider can prescribe your treatments.",
    },
  },
};

export const COMMON_REASONS_FOR_VERIFICATION_FAILURE = `#### Common reasons for verification failure

- **Name mismatch:** The name you enter must match the name on your government ID exactly.
- **Photo issues:** Upload a clear photo of the front of your government ID and a selfie. Neither can be blurry, and the ID must be readable.
- **Missing document:** Make sure you upload both your government ID and a selfie.`;

export const BANNER_MESSAGES = {
  ID_VERIFICATION_FAILED: `**ID verification failed, <u>click here to reupload</u>.**

${COMMON_REASONS_FOR_VERIFICATION_FAILURE}`,
  INTAKE_INCOMPLETE:
    "Request cannot be processed until clinical intake is complete, <u>click here to complete</u>.",
  GENERIC_ERROR: "Reach out to care@evvy.com if you have questions.",
  REFERRAL_ERROR:
    "Your consult has been canceled. If this was unexpected, please reach out to care@evvy.com",
  CONSULT_ERROR:
    "Something was wrong with your consult. Reach out to care@evvy.com if you have questions.",
  INELIGIBLE_ERROR:
    "You were ineligible for treatment. Reach out to care@evvy.com if you have questions.",
  CANCELLED_ERROR:
    "Your consult was cancelled. Reach out to care@evvy.com if you have questions.",
  TURNAROUND_TIME: (turnaroundTime: string) =>
    `You should hear back from a clinician within ${turnaroundTime} with your finalized treatment.`,
};

export const MESSAGE_THREAD_COPY = {
  NEW_MESSAGE: "You have a new message:",
  VIEW_ARROW: "View ->", // TODO: should we use the SVG?
  ASK_PROVIDER: "Additional information to share?",
  MESSAGE_PROVIDER_CTA: "Message your provider now",
};

export const CONSULT_DETAILS_LABELS = {
  TEST_ID: "Test ID",
  ORDER_NUMBER: "Order #",
  ORDER_DATE: "Order Date",
  TEST_DATE: "Test Date",
  CLINICAL_INTAKE: "Clinical Intake",
  INTAKE_FORM: "Intake Form",
  VIEW_LINK: "View",
  ORDER_PLACED: "Order placed",
};

export const TREATMENT_COPY = {
  VIEW_TREATMENTS: "View my treatments →",
  TRACK_ORDER: "TRACK YOUR ORDER",
};

// Status code mappings for better maintainability
export const CONSULT_STATUS = {
  OPTED_IN: "IN",
  ORDERED: "OR",
  INTAKE_STARTED: "ST",
  SUBMITTED: "SU",
  IN_PROGRESS: "PR",
  COMPLETE: "CP",
  ERROR: "ER",
  REFERRED: "RF",
  INELIGIBLE: "IE",
  ID_FAILURE: "ID",
  CANCELLED: "CA",
  EXPIRED: "EX",
} as const;

// Status groups for easier conditional logic
export const STATUS_GROUPS = {
  BANNER_WARNING: [
    CONSULT_STATUS.ID_FAILURE,
    CONSULT_STATUS.ORDERED,
    CONSULT_STATUS.INTAKE_STARTED,
  ] as const,
  INTAKE_INCOMPLETE: [
    CONSULT_STATUS.OPTED_IN,
    CONSULT_STATUS.ORDERED,
    CONSULT_STATUS.INTAKE_STARTED,
  ] as const,
  HIDE_INTAKE_LINK: [CONSULT_STATUS.OPTED_IN, CONSULT_STATUS.ORDERED] as const,
} as const;

// Helper functions for type-safe status checking
export const isBannerWarningStatus = (status: string): boolean => {
  return STATUS_GROUPS.BANNER_WARNING.includes(status as any);
};

export const isIntakeIncompleteStatus = (status: string): boolean => {
  return STATUS_GROUPS.INTAKE_INCOMPLETE.includes(status as any);
};

export const shouldHideIntakeLink = (
  status: string,
  isMalePartner?: boolean
): boolean => {
  // For male partners with OPTED_IN status, always show the intake link
  if (isMalePartner && status === CONSULT_STATUS.OPTED_IN) {
    return false;
  }

  return STATUS_GROUPS.HIDE_INTAKE_LINK.includes(status as any);
};

// Helper function to get status-specific messaging
export const getConsultStatusMessages = (
  status: string,
  isMalePartner: boolean,
  consultPaidButIntakeIncomplete: boolean,
  hasOrders: boolean = true
) => {
  if (consultPaidButIntakeIncomplete) {
    const intakeMessage = isMalePartner
      ? CONSULT_STATUS_MESSAGES.MALE_PARTNER.SECONDARY.INTAKE_REQUIRED
      : CONSULT_STATUS_MESSAGES.STANDARD.SECONDARY.INTAKE_REQUIRED;

    return {
      primary: isMalePartner
        ? CONSULT_STATUS_MESSAGES.MALE_PARTNER.PRIMARY.INTAKE_INCOMPLETE
        : CONSULT_STATUS_MESSAGES.STANDARD.PRIMARY.INTAKE_INCOMPLETE,
      secondary: intakeMessage,
    };
  }

  if (isMalePartner) {
    // Handle male partner without orders - show "order not complete" messaging
    if (!hasOrders) {
      return {
        primary: "Complete your order",
        secondary:
          "Your order is not complete. Continue to complete your male partner treatment order.",
      };
    }

    switch (status) {
      case CONSULT_STATUS.OPTED_IN:
      case CONSULT_STATUS.ORDERED:
      case CONSULT_STATUS.INTAKE_STARTED:
        return {
          primary:
            CONSULT_STATUS_MESSAGES.MALE_PARTNER.PRIMARY.INTAKE_INCOMPLETE,
          secondary:
            CONSULT_STATUS_MESSAGES.MALE_PARTNER.SECONDARY.INTAKE_NEEDED,
        };
      case CONSULT_STATUS.SUBMITTED:
        return {
          primary: CONSULT_STATUS_MESSAGES.MALE_PARTNER.PRIMARY.UNDER_REVIEW,
          secondary:
            CONSULT_STATUS_MESSAGES.MALE_PARTNER.SECONDARY.UNDER_REVIEW,
        };
      case CONSULT_STATUS.IN_PROGRESS: // Fixed: was checking for "FN"
        return {
          primary: CONSULT_STATUS_MESSAGES.MALE_PARTNER.PRIMARY.FINALIZING,
          secondary: CONSULT_STATUS_MESSAGES.MALE_PARTNER.SECONDARY.FINALIZING,
        };
      case CONSULT_STATUS.COMPLETE:
        return {
          primary: CONSULT_STATUS_MESSAGES.MALE_PARTNER.PRIMARY.COMPLETE,
          secondary: "",
        };
      case CONSULT_STATUS.ERROR:
      case CONSULT_STATUS.REFERRED:
      case CONSULT_STATUS.INELIGIBLE:
      case CONSULT_STATUS.ID_FAILURE:
      case CONSULT_STATUS.EXPIRED:
        return {
          primary: CONSULT_STATUS_MESSAGES.MALE_PARTNER.PRIMARY.FINALIZING,
          secondary: CONSULT_STATUS_MESSAGES.MALE_PARTNER.SECONDARY.FINALIZING,
        };
      default:
        return {
          primary:
            CONSULT_STATUS_MESSAGES.MALE_PARTNER.PRIMARY.INTAKE_INCOMPLETE,
          secondary:
            CONSULT_STATUS_MESSAGES.MALE_PARTNER.SECONDARY.INTAKE_NEEDED,
        };
    }
  }

  // Standard consult messaging
  switch (status) {
    case CONSULT_STATUS.COMPLETE:
      return {
        primary: CONSULT_STATUS_MESSAGES.STANDARD.PRIMARY.COMPLETE,
        secondary: "",
      };
    case CONSULT_STATUS.ID_FAILURE:
      return {
        primary:
          CONSULT_STATUS_MESSAGES.STANDARD.PRIMARY.ID_VERIFICATION_FAILED,
        secondary:
          CONSULT_STATUS_MESSAGES.STANDARD.SECONDARY.ID_VERIFICATION_FAILED,
      };
    default:
      return {
        primary: CONSULT_STATUS_MESSAGES.STANDARD.PRIMARY.IN_PROGRESS,
        secondary: CONSULT_STATUS_MESSAGES.STANDARD.SECONDARY.DEFAULT,
      };
  }
};

export const getBannerMessage = (
  status: string,
  consultPaidButIntakeIncomplete: boolean,
  isMalePartner: boolean,
  turnaroundTime: string,
  hasOrders: boolean = true
) => {
  if (status === CONSULT_STATUS.ID_FAILURE) {
    return BANNER_MESSAGES.ID_VERIFICATION_FAILED;
  }

  // Handle male partner without orders
  if (isMalePartner && !hasOrders) {
    return "Order not complete";
  }

  // Show specific error messages for male partner error statuses
  if (isMalePartner) {
    if (status === CONSULT_STATUS.ERROR) {
      return BANNER_MESSAGES.CONSULT_ERROR;
    }
    if (status === CONSULT_STATUS.REFERRED) {
      return BANNER_MESSAGES.REFERRAL_ERROR;
    }
    if (status === CONSULT_STATUS.INELIGIBLE) {
      return BANNER_MESSAGES.INELIGIBLE_ERROR;
    }
    if (status === CONSULT_STATUS.CANCELLED) {
      return BANNER_MESSAGES.CANCELLED_ERROR;
    }
  }

  if (
    consultPaidButIntakeIncomplete ||
    (isMalePartner && STATUS_GROUPS.INTAKE_INCOMPLETE.includes(status as any))
  ) {
    return BANNER_MESSAGES.INTAKE_INCOMPLETE;
  }

  return BANNER_MESSAGES.TURNAROUND_TIME(turnaroundTime);
};

/**
 * Check if a consult status indicates completion (finished, no more action needed)
 * @param status - The consult status to check
 * @returns True if the consult is in a completed state
 */
export const isConsultCompleted = (status: string) => {
  return (
    status === CONSULT_STATUS.COMPLETE ||
    status === CONSULT_STATUS.REFERRED ||
    status === CONSULT_STATUS.SUBMITTED
  );
};

/**
 * Check if a male partner consult should show the special error banner styling
 * @param isMalePartner - Whether this is a male partner consult
 * @param consultStatus - The consult status
 * @param consultPaidButIntakeIncomplete - Whether consult is paid but intake incomplete
 * @returns True if should show male partner error banner
 */
export const shouldShowMalePartnerErrorBanner = (
  isMalePartner: boolean,
  consultStatus: string,
  consultPaidButIntakeIncomplete: boolean
) => {
  return (
    isMalePartner &&
    (consultPaidButIntakeIncomplete ||
      consultStatus === CONSULT_STATUS.OPTED_IN ||
      consultStatus === CONSULT_STATUS.INTAKE_STARTED ||
      consultStatus === CONSULT_STATUS.ORDERED ||
      consultStatus === CONSULT_STATUS.ERROR ||
      consultStatus === CONSULT_STATUS.REFERRED ||
      consultStatus === CONSULT_STATUS.INELIGIBLE ||
      consultStatus === CONSULT_STATUS.CANCELLED)
  );
};

/**
 * Get the male partner intake view URL for a consult
 * @param consultId - The consult ID
 * @returns The URL to view the male partner intake form
 */
export const getMalePartnerIntakeViewUrl = (consultId: string) => {
  return `/treatments/consults/${consultId}/male-partner/intake/intro`;
};
